/**
 * Content for the bilingual answers page (Turkish at /sss/, English at
 * /en/faq/), rendered by src/components/AnswersPage.tsx with FAQPage
 * structured data.
 *
 * Purpose: give station owners, Google and AI assistants (ChatGPT, Claude,
 * Perplexity) short, self-contained, quotable answers to the questions fuel
 * station owners actually ask, each linking to the product page or guide that
 * covers the topic in depth.
 *
 * Rule: only facts already published on the site. Every statement about the
 * product (what it does, integrations, how matching works, timings,
 * conditions, limits, prices) must already appear in src/content/blogPosts.ts,
 * src/content/landingPages.ts, src/content/legal.ts, src/util/languageData.tsx,
 * scripts/llms-intro.md or src/content/productFacts.json. Each item lists its
 * sources in a "// Sources:" comment (languageData.tsx line numbers drift, so
 * the key path is given too). Never add a metric, customer, timing or
 * condition the site does not already state; leave `limitations` out instead
 * of guessing. Prices come only from PRICE_TEXT. Links are final trailing-slash
 * URLs of prerendered pages (package.json reactSnap.include), Turkish items to
 * Turkish pages and English items to English pages.
 *
 * AnswersPage prefixes `example` with the "Örnek" / "Example" label, so the
 * example text itself does not repeat it; amounts there are illustrative and
 * stay below 1,000 (src/seo/productFacts.test.ts flags larger TRY amounts).
 */

import { PRICE_TEXT } from "./productFacts";

/** Internal link from an answer to the page that covers it in depth. */
export interface AnswerLink {
  to: string;
  label: string;
}

/** One question with a quotable answer. */
export interface AnswerItem {
  /** Stable ASCII kebab-case anchor, the same in tr and en for the same question. */
  id: string;
  /** Phrased the way a station owner would type it into Google or ChatGPT. */
  question: string;
  /** 1-3 sentences, self-contained (makes sense quoted alone). */
  answer: string;
  /** For "how" questions: 3-6 short numbered steps. */
  steps?: string[];
  /** Optional short, illustrative worked example. */
  example?: string;
  /** Optional honest conditions/limits, only when the sources state them. */
  limitations?: string;
  /** 1-3 internal links. */
  links: AnswerLink[];
}

/** One language version of the answers page. */
export interface AnswersLocale {
  /** <title>, 25-60 characters including the " - Shift Tracker" suffix. */
  title: string;
  /** Meta description, 70-160 characters. */
  description: string;
  h1: string;
  /** 1-2 sentences under the h1. */
  intro: string;
  items: AnswerItem[];
}

export const ANSWERS: { tr: AnswersLocale; en: AnswersLocale } = {
  tr: {
    title: "Akaryakıt İstasyonu: Sık Sorulan Sorular - Shift Tracker",
    description:
      "İstasyon sahiplerinin sorularına kısa cevaplar: TURPAK satışlarını bankayla eşleştirme, valör, nakit yatırma doğrulama, personel açığı ve Z raporu.",
    h1: "Akaryakıt istasyonu sahiplerinin sorduğu sorular",
    intro:
      "Vardiya, kasa ve banka mutabakatı hakkında istasyon sahiplerinin en sık sorduğu sorulara kısa ve net cevaplar. Her cevap, konuyu ayrıntılı anlatan rehbere veya ürün sayfasına bağlanır.",
    items: [
      // Sources: blogPosts.ts:694 (aynı tutar, aynı terminal, dar zaman aralığı; tek hareket;
      // belirsizler incelemeye), blogPosts.ts:420-428 (adım adım eşleştirme),
      // landingPages.ts:130-133 (otomatik aktarım, banka dosyaları), blogPosts.ts:699 (tüm aktif
      // bankaların dosyası beklenir), blogPosts.ts:941 (gün sonu dosyası çoğunlukla ertesi sabah),
      // landingPages.ts:972 (bankaya ulaşmayan kart satışı incelemeye çıkar)
      {
        id: "match-turpak-sales-with-bank",
        question: "TURPAK satışlarını banka ödemeleriyle nasıl eşleştiririm?",
        answer:
          "Shift Tracker, TURPAK veya Asis otomasyonundan gelen her kart satışını bankanın POS hareketiyle eşleştirir: aynı tutar, aynı terminal ve dar bir zaman aralığı. Bir satış yalnızca tek bir banka hareketiyle eşleşebildiği için aynı ödeme iki kez sayılamaz; güvenli eşleşmeler onaylanır, belirsiz olanlar incelemeye bırakılır.",
        steps: [
          "Satışlar pompa otomasyonundan (TURPAK, Asis) vardiyaya otomatik aktarılır.",
          "Bankaların gün sonu dosyaları gelince kart satışları banka onaylı ödemelerle yan yana karşılaştırılır.",
          "Nakit girilmiş ama kartla ödenmiş satışların ödeme tipi düzeltilir (nakit ↔ kart).",
          "Eşleşmeyen veya belirsiz satırlar incelenir; tahsisler oluşturulup kalan tutar çözüme bağlanır.",
          "Her düzeltme gerekçe, inceleyen ve zaman damgasıyla kaydedilir.",
        ],
        example:
          "Otomasyonda 450 TL'lik bir kart satışı olduğunu düşünün. Bankanın dosyasında aynı terminalden, aynı tutarda ve satışa yakın saatte tek bir 450 TL'lik POS hareketi varsa ikisi eşleşir ve satış banka onaylı olur; böyle bir hareket yoksa satış inceleme için öne çıkar.",
        limitations:
          "Eşleştirme anlık değildir: bankanın gün sonu mutabakat dosyası gerekir ve bu dosyalar çoğunlukla ertesi sabah gelir. Bir günün mutabakatı, o güne ait tüm aktif bankaların dosyaları gelene kadar bekletilir; eksik resimle eşleştirme yapılmaz.",
        links: [
          {
            to: "/blog/turpak-satis-banka-eslestirme/",
            label: "TURPAK satışını banka ödemeleriyle eşleştirme rehberi",
          },
          {
            to: "/blog/shift-tracker-mutabakati-nasil-calisir/",
            label: "Shift Tracker mutabakatı nasıl çalışır?",
          },
        ],
      },
      // Sources: blogPosts.ts:917-935 (valör, blokaj, komisyon, taksit, iade/iptal),
      // blogPosts.ts:941 (gün sonu dosyası, planlı mutabakat), blogPosts.ts:956 (aynı gün
      // eşit olmaması normaldir)
      {
        id: "when-card-sales-reach-bank",
        question: "Kredi kartı satışı bankaya ne zaman geçer? Valör ve blokaj nedir?",
        answer:
          "Kart satışı anında yapılsa da para hesaba anında geçmez: banka tutarı valör (değer) gününde aktarır, bu yüzden bir vardiyanın kart satışları çoğu zaman ancak ertesi gün banka tarafında görünür. Valör işlemin hesaba gerçekten geçtiği gündür, blokaj ise tutarın hesaba aktarılmadan önce bekletildiği süredir; komisyon, taksit, iade ve iptal de POS toplamını banka tutarından ayırır. Shift Tracker bu yüzden kart satışlarını, bankanın gün sonu mutabakat dosyası geldikten sonra planlı olarak eşleştirir.",
        example:
          "Bir vardiyada POS'ta 800 TL kart satışı göründüğünü düşünün. Bu tutar bankada genellikle ertesi gün görünür ve banka komisyonu kestiği için hesaba geçen net tutar 800 TL'den azdır; iki rakamın aynı gün birebir tutmaması normaldir.",
        links: [
          {
            to: "/blog/kredi-karti-satisi-bankaya-ne-zaman-gecer/",
            label: "Kredi kartı satışı bankaya ne zaman geçer? Valör ve blokaj rehberi",
          },
          {
            to: "/kredi-karti-odeme-dogrulama/",
            label: "Otomatik kredi kartı ödeme doğrulama",
          },
        ],
      },
      // Sources: landingPages.ts:804 (benzersiz referans, beklenen tutar, banka günü),
      // landingPages.ts:809 (güvenli API, referans ve tam tutar), landingPages.ts:814 (aynı gün,
      // dakikalar ila birkaç saat), landingPages.ts:819 (eksik, mükerrer, gelmeyen: uyarı),
      // landingPages.ts:824 (slip, SMS, e-posta), landingPages.ts:837 (bankada yazılacak referans),
      // landingPages.ts:842 (yaklaşık 30 dakikada bir, hafta sonu/tatil), landingPages.ts:847
      // (referans yazılmazsa elle doğrulama), landingPages.ts:852 (yönetici elle doğrular/kaldırır),
      // landingPages.ts:857 (ilk entegrasyon VakıfBank), blogPosts.ts:704 (referans dekonta
      // yazılır), languageData.tsx:4100 (tr.home.faq, aynı gün otomatik doğrulama)
      {
        id: "verify-cash-deposits",
        question: "Nakit yatırmaların gerçekten bankaya ulaştığını nasıl doğrularım?",
        answer:
          "Shift Tracker'da nakit yatırıldı olarak işaretlendiğinde uygulama, bankada yazılacak benzersiz bir referans üretir; sistem banka hesabınızı güvenli bir API ile izler ve bu referansı ve tam tutarı taşıyan para girişini gördüğünde yatırmayı otomatik doğrular. Eksik, mükerrer veya gün sonuna kadar gelmeyen yatırmalar aynı gün incelenmek üzere işaretlenir.",
        steps: [
          "Nakit uygulamada yatırıldı olarak işaretlenir; uygulama beklenen tutar ve banka günüyle birlikte benzersiz bir referans kodu üretir.",
          "Kod, yatırmayı yapan kişiye yazdırılabilir yatırma slibinde, SMS ve e-postayla ulaşır.",
          "Yatırma yapılırken bu referans bankada dekonta yazılır.",
          "Shift Tracker banka hesabını yaklaşık 30 dakikada bir kontrol eder ve referansı ve tam tutarı taşıyan para girişini arar.",
          "Güvenli bir eşleşmede yatırma kendiliğinden doğrulanır; tutar uyuşmazlığı, mükerrer kayıt veya gelmeyen yatırma uyarı oluşturur.",
        ],
        example:
          "C4S1D06062601 gibi bir referansla 900 TL'nin yatırıldı olarak işaretlendiğini düşünün. Bankada bu referansla 900 TL'lik bir para girişi görünürse yatırma doğrulanır; giriş 700 TL ise tutar uyuşmazlığı olarak işaretlenir.",
        limitations:
          "Doğrulama anlık değil, aynı gündür: önce bankanın işlemi işlemesi gerekir, bu genellikle dakikalar ila birkaç saat sürer ve hafta sonu veya tatil yatırmaları bir sonraki iş günü doğrulanır. Referans bankada yazılmaz veya yanlış yazılırsa yatırma otomatik eşleşmez; inceleme için raporlanır ve elle doğrulanır. İlk banka entegrasyonu VakıfBank'tır ve yöneticiler bir yatırmayı her zaman elle doğrulayabilir veya doğrulamayı kaldırabilir.",
        links: [
          {
            to: "/nakit-yatirma-dogrulama/",
            label: "Otomatik nakit yatırma doğrulama",
          },
        ],
      },
      // Sources: languageData.tsx:4126 (tr.home.faq, farklar gerekçe, inceleyen ve zaman
      // damgasıyla vardiyaya ve personele atanır), blogPosts.ts:263-272 (mutabakat adımları; veri
      // girişi hatası, ödeme tipi yanlışlığı veya gerçek açık), blogPosts.ts:604-609 (açık gerekçeyle
      // atanır, vardiya kilitlenir), blogPosts.ts:709 (eşleşti / inceleme / açık durumları),
      // landingPages.ts:130-134 (aktarım ve eşleştirme), landingPages.ts:384 (nakit, bankaya yatan
      // tutar üzerinden teyit edilir)
      {
        id: "employee-cash-deficits",
        question: "Personel (pompacı) kasa açığı nasıl hesaplanır ve takip edilir?",
        answer:
          "Shift Tracker her vardiyayı banka onaylı ödemeler ve ERP (otomasyon) toplamlarıyla mutabık kılar; mutabakattan sonra kalan fark gerekçe, inceleyen ve zaman damgasıyla birlikte ilgili vardiyaya ve personele atanır. Bu kayıtlar, farkın bir veri giriş hatası mı, bir ödeme tipi yanlışlığı mı yoksa gerçek bir açık mı olduğunu netleştirir.",
        steps: [
          "Vardiyanın satışları otomasyondan gelir; kart satışları banka onaylı ödemelerle eşleştirilir.",
          "Nakit tarafı, bankaya yatan tutar üzerinden doğrulanır.",
          "Yanlış girilmiş ödeme tipleri düzeltilir.",
          "Kalan fark açık bir gerekçeyle ilgili vardiyaya ve personele atanır; satır açık durumunda görünür.",
          "Vardiya kilitlenir; açık kaybolmak yerine denetlenebilir bir kayıt olarak kalır.",
        ],
        example:
          "Bir vardiyada kart satışları bankayla eşleştikten ve ödeme tipleri düzeltildikten sonra 300 TL'lik çözülmemiş bir fark kaldığını düşünün. Bu 300 TL, gerekçesiyle birlikte o vardiyaya ve ilgili personele açık olarak atanır ve kimin neyi onayladığı kayıtta kalır.",
        links: [
          {
            to: "/blog/vardiya-mutabakati-ve-personel-acigi/",
            label: "Vardiya mutabakatı ve personel açığı yönetimi",
          },
          {
            to: "/benzin-istasyonu-yazilimi/",
            label: "Benzin istasyonu yazılımı",
          },
        ],
      },
      // Sources: blogPosts.ts:1031 (Z raporu gün sonunda, ör. 23:59), blogPosts.ts:1036 (vardiya
      // raporu tek personelin süresini kapsar), blogPosts.ts:1042-1045 (gün kayması, ödeme tipi,
      // vardiya ortası nakit, iade zamanlaması), blogPosts.ts:1052 (iki kaynağı vardiya bazında
      // eşleştirmek), blogPosts.ts:1067 (arıza değil, farklı zaman aralıkları)
      {
        id: "z-report-vs-shift-report",
        question: "Z raporu neden vardiya raporuyla tutmuyor?",
        answer:
          "Z raporu genellikle gün sonunda (ör. 23:59) alınır ve günün tüm işlemlerini tek pakette toplar; vardiya raporu ise tek bir personelin çalıştığı süreyi kapsar, bu yüzden ikisi aynı zaman aralığına bakmaz ve tutarlar kaymış görünür. Nakit girilmiş kart satışları, devir sırasında alınan nakit ve sonradan yapılan iadeler de farkı büyütür. Shift Tracker farkı kağıt rapora göre değil, otomasyon satışlarını banka kayıtlarıyla her vardiya bazında eşleştirerek çözer.",
        example:
          "Gündüz vardiyasının 08:00-16:00 arasında olduğunu, Z raporunun ise 23:59'da kesildiğini düşünün. Z raporundaki kart toplamı 16:00'dan sonraki satışları da içerdiği için gündüz vardiyasının kart toplamıyla tutmaz; bu bir arıza değil, farklı zaman aralıklarının sonucudur.",
        links: [
          {
            to: "/blog/z-raporu-vardiya-raporu-neden-tutmaz/",
            label: "Z raporu ile vardiya raporu neden tutmaz?",
          },
          {
            to: "/blog/kredi-karti-gun-sonu-raporu-banka-mutabakati/",
            label: "Kredi kartı gün sonu raporu banka ile eşleşiyor mu?",
          },
        ],
      },
      // Sources: blogPosts.ts:507 (nakit girilen kart satışı için sistem nakit bekler),
      // blogPosts.ts:512 (bankayla karşılaştırılmazsa toplam tutarlı görünür), blogPosts.ts:518-519
      // (eşleştirme uyumsuzluğu gösterir; düzeltme orijinal kaydı saklar), languageData.tsx:3974
      // (tr.home.features, eşleşmeyen her tutar öne çıkarılır), languageData.tsx:4387
      // (tr.fraud.gaps), languageData.tsx:4053 (tr.home.examples, düzeltilen satış mutabakatta
      // görünür ve banka hareketine bağlanabilir), landingPages.ts:146 (orijinal tip, yeni tip,
      // gerekçe, kullanıcı, zaman), languageData.tsx:5066 (tr ödeme tipi düzeltme sayfası: banka
      // eşleştirmesi oluşturmaz)
      {
        id: "card-sale-recorded-as-cash",
        question: "Kartla ödenen bir satışın nakit girildiğini nasıl anlarım?",
        answer:
          "Satışlar banka kayıtlarıyla karşılaştırılmadığında kartla ödenip nakit girilen bir satış fark edilmez, çünkü otomasyon toplamı kendi içinde tutarlı görünür. Shift Tracker otomasyon satışlarını banka onaylı ödemelerle eşleştirir ve eşleşmeyen her tutarı öne çıkarır; böylece raporlanan ödeme tipi ile gerçekte bankaya geçen tutar arasındaki uyumsuzluk ortaya çıkar. Satış karta düzeltildiğinde orijinal ödeme tipi, yeni ödeme tipi, gerekçe, kullanıcı ve zaman kayıt altında kalır.",
        example:
          "Müşterinin 600 TL'yi kartla ödediğini ama satışın otomasyona nakit girildiğini düşünün. Kayıtlara göre bu 600 TL kasada nakit beklenir, bankada ise otomasyonda karşılığı olan bir kart satışı bulunmayan 600 TL'lik bir POS hareketi vardır; bu eşleşmeyen tutar mutabakatta öne çıkar. Satış karta düzeltilince mutabakatta görünür hale gelir ve banka hareketine bağlanabilir.",
        limitations:
          "Ödeme tipi düzeltmesi tek başına banka eşleştirmesi oluşturmaz: düzeltilen satış mutabakat çalışma alanında görünür hale gelir ve banka hareketine orada bağlanır.",
        links: [
          {
            to: "/blog/kart-satisini-nakit-gosterme-suistimali/",
            label: "Kart satışını nakit gösterme suistimali",
          },
          {
            to: "/fraud-prevention/",
            label: "Akaryakıt istasyonunda suistimal önleme",
          },
        ],
      },
      // Sources: blogPosts.ts:584 (kart tarafı arka planda, gün sonu dosyalarıyla),
      // blogPosts.ts:589-609 (vardiyayı aç, gözden geçir, nakit yatırmayı doğrula, farkları çöz,
      // kilitle), blogPosts.ts:614 (zor iş önceden otomatik yapılır), languageData.tsx:4114
      // (tr.home.faq: çoğu vardiya hazır gelir; kağıt ve Excel ile 1,5-2 saat; manuel düzeltme nadir
      // istisna), landingPages.ts:428 (çoğu vardiya saniyeler, en fazla birkaç dakika),
      // blogPosts.ts:699 (tüm aktif bankaların dosyası beklenir), blogPosts.ts:941 (dosyalar
      // çoğunlukla ertesi sabah)
      {
        id: "close-shift-under-a-minute",
        question: "Vardiya bir dakikanın altında nasıl kapatılır?",
        answer:
          "Shift Tracker'da eşleştirme ve doğrulama siz vardiyayı açmadan önce otomatik yapılır: kart satışları bankaların gün sonu dosyalarıyla arka planda eşleştirilir, nakit yatırma banka ekstresiyle doğrulanır. Size yalnızca işaretli satırları gözden geçirmek, varsa farkları gerekçesiyle atamak ve vardiyayı kilitlemek kalır; kağıt ve Excel ile 1,5-2 saat süren vardiya alma böylece bir dakikanın altına iner.",
        steps: [
          "Panelde vardiyayı açın: ERP toplamları, banka onaylı kart tutarları ve nakit hareketleri tek ekranda gelir, hiçbir rakamı elle girmezsiniz.",
          "Rakamları gözden geçirin: eşleşen satırlar yeşildir, yalnızca eksik, fazla veya belirsiz olarak işaretlenen satırlara bakarsınız.",
          "Nakit yatırmanın banka ekstresiyle doğrulandığını kontrol edin.",
          "Çözülmemiş farkları gerekçesiyle ilgili vardiyaya ve personele atayın.",
          "Vardiyayı kilitleyin: kilitli vardiya değiştirilemez ve kimin neyi onayladığıyla birlikte kalıcı kayıt olarak saklanır.",
        ],
        limitations:
          "Bir dakikanın altındaki süre, kart tarafının siz açmadan önce mutabık kılınmış olmasına dayanır: bankaların gün sonu dosyaları çoğunlukla ertesi sabah gelir ve bir günün mutabakatı o güne ait tüm aktif bankaların dosyaları gelene kadar bekletilir. Bu süre çoğu vardiya içindir; çoğu vardiya saniyeler içinde, en fazla birkaç dakikada kapanır ve manuel düzeltme yalnızca nadir istisnalarda gerekir.",
        links: [
          {
            to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/",
            label: "Bir vardiyayı bir dakikanın altında nasıl kapatırsınız",
          },
          {
            to: "/vardiya-alma-programi/",
            label: "Vardiya alma programı",
          },
        ],
      },
      // Sources: productFacts.json (integrations: TURPAK, Asis), landingPages.ts:100-101 (TURPAK &
      // Asis entegrasyonu), landingPages.ts:145 (otomasyonun yerini almaz), languageData.tsx:4107
      // (tr.home.faq: otomasyon ölçer, Shift Tracker bankaya geçtiğini kanıtlar),
      // languageData.tsx:4346 (tr.about.why: pompada hiçbir şey değişmez), landingPages.ts:857
      // (ilk banka entegrasyonu VakıfBank, başka bankalar eklenebilir)
      {
        id: "supported-automation-systems",
        question: "Shift Tracker hangi akaryakıt otomasyon sistemleriyle çalışır?",
        answer:
          "Shift Tracker, TURPAK ve Asis pompa otomasyon sistemleriyle çalışır: satış verisini otomasyondan otomatik alır ve banka onaylı ödemelerle mutabık kılar. Otomasyonun yerini almaz ve pompada hiçbir şey değişmez; otomasyon satışı pompada ölçer, Shift Tracker o satışın bankaya geçtiğini kanıtlar.",
        limitations:
          "Nakit yatırma doğrulama tarafında ilk banka entegrasyonu VakıfBank'tır; tasarım zamanla başka bankaların eklenmesini destekler.",
        links: [
          {
            to: "/blog/turpak-otomasyonu-banka-mutabakati/",
            label: "TURPAK otomasyonu ve banka mutabakatı",
          },
          {
            to: "/akaryakit-istasyonu-yazilimi/",
            label: "Akaryakıt istasyonu yazılımı",
          },
        ],
      },
      // Sources: blogPosts.ts:339 (otomasyon litre, stok, vardiya, pompa-tank verisi üretir; banka
      // ile karşılaştırmaz), blogPosts.ts:345 (otomasyondaki kart satışı bankaya geçmemiş olabilir),
      // blogPosts.ts:358 (otomasyon ölçer, Shift Tracker kanıtlar), landingPages.ts:145 (yerini
      // almaz, eksik banka mutabakatını ekler), landingPages.ts:972 (bankaya ulaşmayan satış
      // incelemeye çıkar), landingPages.ts:660 ve 694 (muhasebe programı değildir)
      {
        id: "automation-vs-reconciliation-software",
        question:
          "İstasyon otomasyon yazılımı ile Shift Tracker gibi bir mutabakat yazılımı arasındaki fark nedir?",
        answer:
          "TURPAK veya Asis gibi pompa ve tank otomasyonu satışı pompada ölçer, kaydeder ve raporlar (litre, stok, vardiya ve pompa-tank verisi); kart satışlarının bankaya geçip geçmediğini, nakit girişlerinin doğruluğunu ve ödeme tiplerinin gerçeğe uygunluğunu banka kayıtlarıyla karşılaştırmaz. Shift Tracker otomasyonun yerini almaz, onun verisini kullanarak eksik olan banka mutabakatını ekler: otomasyon satışı ölçer, Shift Tracker o satışın bankaya geçtiğini kanıtlar.",
        example:
          "Otomasyonda kartla ödenmiş görünen 250 TL'lik bir satış, otomasyon açısından tamamdır. Shift Tracker ise bu satışın karşılığında bankada gerçekten 250 TL'lik bir POS hareketi olup olmadığına bakar; yoksa satış inceleme için öne çıkar.",
        limitations:
          "Shift Tracker tam bir muhasebe programı (e-fatura, e-defter, genel muhasebe) da değildir; mevcut muhasebe programınızla birlikte çalışan, banka onaylı mutabakat katmanıdır.",
        links: [
          {
            to: "/akaryakit-istasyonu-yazilimi/",
            label: "Akaryakıt istasyonu yazılımı rehberi",
          },
          {
            to: "/akaryakit-istasyonu-muhasebe-programi/",
            label: "Akaryakıt istasyonu muhasebe programı ve mutabakat",
          },
        ],
      },
      // Sources: landingPages.ts:163-164 (tek plan, her şey dahil; aylık/yıllık fiyat, KDV dahil;
      // aylık planda taahhüt yok; koşulsuz para iade), landingPages.ts:204, languageData.tsx:4234-4248
      // (tr.pricing: dahil olanlar, KDV dahil, indirim sınırlı süreli, liste fiyatı),
      // legal.ts:104-106 (ilk abonelik ayı, 30 gün koşulsuz iade), llms-intro.md:14
      {
        id: "pricing",
        question: "Shift Tracker'ın fiyatı ne kadar?",
        answer: `Shift Tracker tek plan ve her şey dahil sunulur: sınırlı süreli indirimle aylık ${PRICE_TEXT.tr.monthly} veya yıllık ${PRICE_TEXT.tr.annual}, KDV dahil. Aylık planda taahhüt yoktur ve ilk ${PRICE_TEXT.tr.moneyBackDays} gün koşulsuz para iade garantisi vardır. Fiyata TURPAK entegrasyonu, kart satışlarının banka ödemeleriyle eşleştirilmesi, nakit yatırma doğrulama, personel açık takibi, canlı satış ekranı, tank stok takibi ve doğrudan WhatsApp ve telefon desteği dahildir.`,
        limitations: `İndirimli fiyat sınırlı bir süre için geçerlidir (liste fiyatı: aylık ${PRICE_TEXT.tr.monthlyList}, yıllık ${PRICE_TEXT.tr.annualList}).`,
        links: [
          {
            to: "/fiyatlandirma/",
            label: "Fiyatlandırma ayrıntıları",
          },
        ],
      },
    ],
  },
  en: {
    title: "Fuel Station FAQ: Reconciliation Answers - Shift Tracker",
    description:
      "Short answers to fuel station owners' questions: matching TURPAK sales with the bank, value dates, deposit verification, employee deficits and Z-reports.",
    h1: "Questions fuel station owners ask",
    intro:
      "Short, direct answers to the questions station owners ask most about shifts, cash and bank reconciliation. Each answer links to the guide or product page that covers it in depth.",
    items: [
      // Sources: blogPosts.ts:739 (same amount, same terminal, narrow time window; one
      // transaction; ambiguous left for review), blogPosts.ts:463-471 (step-by-step match),
      // landingPages.ts:280-283 (automatic import, bank files), blogPosts.ts:744 (waits for every
      // active bank's file), blogPosts.ts:994 (end-of-day file usually lands the next morning),
      // landingPages.ts:1049 (a card sale that never reached the bank is surfaced for review)
      {
        id: "match-turpak-sales-with-bank",
        question: "How do I match TURPAK sales with bank payments?",
        answer:
          "Shift Tracker matches every card sale from your TURPAK or Asis automation to the bank's POS transaction with the same amount, the same terminal and a narrow time window. A sale can match only one bank transaction, so the same payment can never be counted twice; confident matches are confirmed and ambiguous ones are left for review.",
        steps: [
          "Sales are imported automatically from your pump automation (TURPAK, Asis) into the shift.",
          "When the banks' end-of-day files arrive, card sales are compared side by side with bank-confirmed payments.",
          "Sales entered as cash but paid by card get their payment type corrected (cash ↔ card).",
          "Unmatched or ambiguous rows are reviewed; allocations are created and the remaining amount is resolved.",
          "Every correction is recorded with its reason, reviewer and timestamp.",
        ],
        example:
          "Say the automation shows a card sale of ₺450. If the bank's file has exactly one ₺450 POS transaction from the same terminal close to the time of sale, the two are matched and the sale becomes bank-confirmed; if there is no such transaction, the sale is surfaced for review.",
        limitations:
          "Matching is not instant: it needs the bank's end-of-day settlement file, which usually lands the next morning. A day's reconciliation is held until every active bank's file for that day has arrived, so nothing is matched against an incomplete picture.",
        links: [
          {
            to: "/en/blog/matching-turpak-sales-with-bank-payments/",
            label: "Matching TURPAK sales with bank payments: a step-by-step guide",
          },
          {
            to: "/en/blog/how-shift-tracker-reconciliation-works/",
            label: "How Shift Tracker reconciliation works",
          },
        ],
      },
      // Sources: blogPosts.ts:970-988 (value date, holds, commission, installments,
      // refunds/cancellations), blogPosts.ts:994 (end-of-day file, scheduled reconciliation),
      // blogPosts.ts:1009 (normal not to match on the same day)
      {
        id: "when-card-sales-reach-bank",
        question: "When do card sales reach the bank? What are value date and hold?",
        answer:
          "A card sale happens instantly, but the money does not reach the account instantly: the bank credits it on a value date, so a shift's card sales usually only appear on the bank side the following day. The value date (valör) is the day a transaction actually credits the account and a hold (blokaj) is the period the amount is kept before it is transferred; commission, installments, refunds and cancellations also separate the POS total from the bank amount. That is why Shift Tracker matches card sales on a schedule, once the bank's end-of-day settlement file has arrived.",
        example:
          "Say the POS shows ₺800 in card sales for a shift. The bank usually shows that amount the next day, and because the bank deducts its commission, the net amount credited is less than ₺800; the two figures not matching on the same day is normal.",
        links: [
          {
            to: "/en/blog/when-do-card-sales-reach-the-bank/",
            label: "When do card sales reach the bank? A guide to settlement timing",
          },
          {
            to: "/en/credit-card-payment-verification/",
            label: "Automatic credit card payment verification",
          },
        ],
      },
      // Sources: landingPages.ts:881 (unique reference, expected amount, bank business day),
      // landingPages.ts:886 (secure API, reference and exact amount), landingPages.ts:891 (same
      // day, minutes to a few hours), landingPages.ts:896 (short, duplicate, missing: alert),
      // landingPages.ts:901 (slip, SMS, email), landingPages.ts:914 (reference written at the bank),
      // landingPages.ts:919 (about every 30 minutes, weekend/holiday), landingPages.ts:924
      // (reference missing: verified by hand), landingPages.ts:929 (admins verify/unverify),
      // landingPages.ts:934 (VakıfBank first), blogPosts.ts:749 (reference on the deposit slip),
      // languageData.tsx:1537 (eng.home.faq, same-day automatic confirmation)
      {
        id: "verify-cash-deposits",
        question: "How do I verify that cash deposits actually reached the bank?",
        answer:
          "In Shift Tracker, when cash is marked deposited the app issues a unique reference to write at the bank; the system watches your bank account through a secure API and confirms the deposit automatically once a money-in transaction with that reference and the exact amount appears. Deposits that are short, duplicated or never arrive by end of day are flagged for review the same day.",
        steps: [
          "Cash is marked deposited in the app, which issues a unique reference code with the expected amount and bank business day.",
          "The depositor gets the code on the printable deposit slip, by SMS and by email.",
          "The reference is written on the deposit at the bank.",
          "Shift Tracker checks the bank account about every 30 minutes for a money-in transaction carrying that reference and the exact amount.",
          "On a confident match the deposit is verified on its own; an amount mismatch, a duplicate or a missing deposit raises an alert.",
        ],
        example:
          "Say ₺900 is marked deposited with a reference such as C4S1D06062601. If a ₺900 money-in transaction with that reference appears at the bank, the deposit is verified; if ₺700 arrives instead, it is flagged as an amount mismatch.",
        limitations:
          "Verification is same-day, not instant: the bank has to post the transaction first, typically within minutes to a few hours, and weekend or holiday deposits verify on the next business day. If the reference is forgotten or mistyped the deposit will not auto-match; it is reported for review and verified by hand. VakıfBank is the first bank integration, and admins can always verify or unverify a deposit by hand.",
        links: [
          {
            to: "/en/deposit-verification/",
            label: "Automatic deposit verification",
          },
        ],
      },
      // Sources: languageData.tsx:1563 (eng.home.faq, differences attributed to the shift and
      // employee with reason, reviewer and timestamp), blogPosts.ts:299-308 (reconciliation steps;
      // data entry error, payment type mistake or genuine deficit), blogPosts.ts:654-659 (attributed
      // with a reason, shift locked), blogPosts.ts:754 (matched / needs review / deficit states),
      // landingPages.ts:280-284 (import and matching), landingPages.ts:451 (cash confirmed against
      // the amount deposited)
      {
        id: "employee-cash-deficits",
        question: "How are employee (pump attendant) cash deficits calculated and tracked?",
        answer:
          "Shift Tracker reconciles each shift against bank-confirmed payments and ERP (automation) totals; the difference left after reconciliation is attributed to the relevant shift and employee with its reason, reviewer and timestamp. That record makes clear whether the difference is a data entry error, a payment type mistake or a genuine deficit.",
        steps: [
          "The shift's sales come in from the automation, and card sales are matched to bank-confirmed payments.",
          "The cash side is confirmed against the amount deposited to the bank.",
          "Misclassified payment types are corrected.",
          "The remaining difference is attributed, with a clear reason, to the relevant shift and employee, and the row shows as a deficit.",
          "The shift is locked, so the deficit becomes an auditable record instead of disappearing.",
        ],
        example:
          "Say that after a shift's card sales have matched the bank and payment types have been corrected, an unresolved difference of ₺300 remains. That ₺300 is attributed, with its reason, to that shift and the relevant employee as a deficit, and the record keeps who approved what.",
        links: [
          {
            to: "/en/blog/shift-reconciliation-employee-deficits/",
            label: "Shift reconciliation and employee deficit management",
          },
          {
            to: "/en/fuel-station-software/",
            label: "Fuel station software",
          },
        ],
      },
      // Sources: blogPosts.ts:1084 (Z-report at day end, e.g. 23:59), blogPosts.ts:1089 (shift
      // report covers one employee's time on duty), blogPosts.ts:1095-1098 (day shift, payment-type
      // mix-up, mid-shift cash, refund timing), blogPosts.ts:1105 (match two sources per shift),
      // blogPosts.ts:1120 (not a fault, different time windows)
      {
        id: "z-report-vs-shift-report",
        question: "Why doesn't the Z-report match the shift report?",
        answer:
          "The Z-report is usually taken at the end of the day (for example 23:59) and totals all of that day's transactions in one batch, while a shift report covers a single employee's time on duty, so the two do not cover the same time window and the totals look shifted. Card sales entered as cash, cash taken during handover and later refunds widen the gap further. Shift Tracker resolves it not from the paper report but by matching automation sales against bank records on a per-shift basis.",
        example:
          "Say the day shift runs 08:00-16:00 and the Z-report is cut at 23:59. The Z-report's card total also includes sales after 16:00, so it will not match the day shift's card total; that is not a fault but the result of different time windows.",
        links: [
          {
            to: "/en/blog/why-z-report-doesnt-match-shift-report/",
            label: "Why your Z-report doesn't match the shift report",
          },
          {
            to: "/en/blog/pos-end-of-day-report-vs-bank-settlement/",
            label: "Does your POS end-of-day report match the bank?",
          },
        ],
      },
      // Sources: blogPosts.ts:543 (a card sale entered as cash: the system expects that amount in
      // cash), blogPosts.ts:548 (without bank comparison the total looks consistent),
      // blogPosts.ts:554-555 (matching exposes the mismatch; correction keeps the original),
      // languageData.tsx:1411 (eng.home.features, anything that does not match is surfaced),
      // languageData.tsx:1824 (eng.fraud.gaps), languageData.tsx:1490 (eng.home.examples, corrected
      // sale becomes visible in reconciliation and can be linked to bank activity),
      // landingPages.ts:296 (original type, new type, reason, user, time), languageData.tsx:2503
      // (eng payment type correction page: does not create bank matching)
      {
        id: "card-sale-recorded-as-cash",
        question: "How do you detect a card sale recorded as cash?",
        answer:
          "When sales are not compared against bank records, a card-paid sale entered as cash goes unnoticed, because the automation total looks internally consistent. Shift Tracker matches automation sales against bank-confirmed payments and surfaces every amount that does not match, which exposes any mismatch between the reported payment type and what actually reached the bank. When the sale is corrected to card, the original payment type, the new type, the reason, the user and the time stay on record.",
        example:
          "Say a customer pays ₺600 by card but the sale is entered in the automation as cash. The records now expect ₺600 in cash, while the bank shows a ₺600 POS transaction with no matching card sale in the automation; that unmatched amount is surfaced in reconciliation. Once the sale is corrected to card, it becomes visible in reconciliation and can be linked to the bank transaction.",
        limitations:
          "Payment type correction does not create the bank match on its own: the corrected sale becomes available in the reconciliation workspace and is linked to the bank transaction there.",
        links: [
          {
            to: "/en/blog/recording-card-sale-as-cash-fraud/",
            label: "Recording a card sale as cash: a common station fraud",
          },
          {
            to: "/en/fraud-prevention/",
            label: "Fraud prevention at fuel stations",
          },
        ],
      },
      // Sources: blogPosts.ts:634 (card side reconciled in the background as end-of-day files
      // arrive), blogPosts.ts:639-659 (open, review, confirm cash deposit, resolve, lock),
      // blogPosts.ts:664 (hard part done automatically beforehand), languageData.tsx:1551
      // (eng.home.faq: most shifts ready; 1.5 to 2 hours on paper and in Excel; manual correction
      // the rare exception), landingPages.ts:495 (most shifts in seconds, a few minutes at most),
      // blogPosts.ts:744 (waits for every active bank's file), blogPosts.ts:994 (files usually land
      // the next morning)
      {
        id: "close-shift-under-a-minute",
        question: "How can a shift be closed in under a minute?",
        answer:
          "In Shift Tracker the matching and verification are done automatically before you open the shift: card sales are reconciled against the banks' end-of-day files in the background, and the cash deposit is verified against the bank statement. All that is left is to review the flagged rows, attribute any difference with a reason and lock the shift, which is how a shift that took 1.5 to 2 hours on paper and in Excel closes in under a minute.",
        steps: [
          "Open the shift in the dashboard: its ERP totals, bank-confirmed card amounts and cash movements come up on one screen, and you type no figures by hand.",
          "Review the figures: matched rows are already green, so you only look at rows flagged as short, over or ambiguous.",
          "Confirm that the cash deposit has been verified against the bank statement.",
          "Attribute any unresolved difference, with a reason, to the relevant shift and employee.",
          "Lock the shift: a locked shift cannot be altered and is kept as a permanent record of who approved what.",
        ],
        limitations:
          "The under-a-minute close relies on the card side being reconciled before you open the shift: the banks' end-of-day files usually land the next morning, and a day's reconciliation waits until every active bank's file for that day has arrived. It describes most shifts, which close in seconds and a few minutes at most; manual correction is only needed for the rare exception.",
        links: [
          {
            to: "/en/blog/how-to-close-a-shift-in-one-minute/",
            label: "How to close a shift in under a minute",
          },
          {
            to: "/en/shift-management-program/",
            label: "Shift management program",
          },
        ],
      },
      // Sources: productFacts.json (integrations: TURPAK, Asis), landingPages.ts:250-251 (TURPAK &
      // Asis integration), landingPages.ts:295 (does not replace the automation),
      // languageData.tsx:1544 (eng.home.faq: automation measures, Shift Tracker proves it reached
      // the bank), languageData.tsx:1783 (eng.about.why: nothing changes at the pump),
      // landingPages.ts:934 (VakıfBank first, more banks over time)
      {
        id: "supported-automation-systems",
        question: "Which station automation systems does Shift Tracker work with?",
        answer:
          "Shift Tracker works with the TURPAK and Asis pump automation systems: it pulls sales data from the automation automatically and reconciles it against bank-confirmed payments. It does not replace your automation and nothing changes at the pump; the automation measures the sale, and Shift Tracker proves that sale reached the bank.",
        limitations:
          "On the cash deposit side, VakıfBank is the first bank integration, and the design supports adding more banks over time.",
        links: [
          {
            to: "/en/blog/turpak-automation-vs-bank-reconciliation/",
            label: "TURPAK automation vs bank reconciliation",
          },
          {
            to: "/en/gas-station-software/",
            label: "Gas station software",
          },
        ],
      },
      // Sources: blogPosts.ts:375 (automation produces litre, inventory, shift, pump-tank data;
      // does not check against the bank), blogPosts.ts:381 (a card sale in automation may never
      // have reached the bank), blogPosts.ts:394 (automation measures, Shift Tracker proves),
      // landingPages.ts:295 (does not replace, adds the missing bank reconciliation),
      // landingPages.ts:1049 (unsettled sale surfaced for review), landingPages.ts:727 and 761 (not
      // an accounting suite)
      {
        id: "automation-vs-reconciliation-software",
        question:
          "What is the difference between station automation software and reconciliation software like Shift Tracker?",
        answer:
          "Pump and tank automation such as TURPAK or Asis measures, records and reports the sale (litre, inventory, shift and pump-tank data); it does not compare card sales against the bank to confirm they settled, check that cash figures are accurate, or verify that reported payment types are truthful. Shift Tracker does not replace the automation: it uses its data and adds the missing bank reconciliation, so the automation measures the sale and Shift Tracker proves it reached the bank.",
        example:
          "Say the automation shows a ₺250 sale paid by card. From the automation's point of view that sale is complete; Shift Tracker checks whether a matching ₺250 POS transaction actually exists at the bank and, if not, surfaces the sale for review.",
        limitations:
          "Shift Tracker is not a full accounting suite (e-invoice, ledger) either; it is the bank-confirmed reconciliation layer that works alongside your existing accounting software.",
        links: [
          {
            to: "/en/gas-station-software/",
            label: "Gas station software guide",
          },
          {
            to: "/en/gas-station-accounting-reconciliation/",
            label: "Gas station accounting reconciliation",
          },
        ],
      },
      // Sources: landingPages.ts:313-314 (one plan, everything included; monthly/annual price, VAT
      // included; no commitment on the monthly plan; unconditional money-back), landingPages.ts:354,
      // languageData.tsx:1671-1685 (eng.pricing: what is included, VAT included, discount for a
      // limited time, list price), legal.ts:255-257 (first subscription month, 30 days,
      // unconditional refund), llms-intro.md:14
      {
        id: "pricing",
        question: "How much does Shift Tracker cost?",
        answer: `Shift Tracker is one plan with everything included: ${PRICE_TEXT.eng.monthly} per month or ${PRICE_TEXT.eng.annual} per year with a limited-time discount, VAT included. The monthly plan has no commitment, and the first ${PRICE_TEXT.eng.moneyBackDays} days come with an unconditional money-back guarantee. The price includes TURPAK integration, card sales matched against bank payments, cash deposit verification, employee deficit tracking, the live sales dashboard, tank stock monitoring and direct WhatsApp and phone support.`,
        limitations: `The discounted price is valid for a limited time (list price: ${PRICE_TEXT.eng.monthlyList} per month, ${PRICE_TEXT.eng.annualList} per year).`,
        links: [
          {
            to: "/en/pricing/",
            label: "Pricing details",
          },
        ],
      },
    ],
  },
};
